import React, { useState, useCallback } from 'react';
import axios from 'axios';
import { QRCodeSVG } from 'qrcode.react';
import { Dialog, DialogContent, DialogHeader, DialogTitle } from '@/components/ui/dialog';
import { Button } from '@/components/ui/button';
import { Input } from '@/components/ui/input';
import { Label } from '@/components/ui/label';
import { Checkbox } from '@/components/ui/checkbox';
import { toast } from 'sonner';
import { CheckCircle2, Copy, Send } from 'lucide-react';
import { API } from '@/App';

const DEFAULT_PERMISSIONS = ['manage_properties', 'respond_messages'];

/* eslint-disable react/prop-types */
/**
 * Self-contained "Invite an Employee" dialog.
 * Owns its own form state + generated-link state so the parent only manages `open` + refresh.
 */
export function InviteDialog({ open, onOpenChange, permissions, onInviteCreated }) {
  const [form, setForm] = useState({ email: '', name: '', permissions: DEFAULT_PERMISSIONS });
  const [generatedLink, setGeneratedLink] = useState(null);

  const reset = useCallback(() => {
    setForm({ email: '', name: '', permissions: DEFAULT_PERMISSIONS });
    setGeneratedLink(null);
  }, []);

  const handleOpenChange = (v) => {
    onOpenChange(v);
    if (!v) reset();
  };

  const togglePermission = (key) => {
    setForm((f) => ({
      ...f,
      permissions: f.permissions.includes(key) ? f.permissions.filter((p) => p !== key) : [...f.permissions, key],
    }));
  };

  const copyLink = async (link) => {
    try {
      await navigator.clipboard.writeText(link);
      toast.success('Invite link copied to clipboard');
    } catch (_e) {
      toast.error(`Copy failed — link: ${link}`);
    }
  };

  const buildInviteLink = (inviteToken) => `${window.location.origin}/invite/accept?token=${inviteToken}`;

  const submit = async (e) => {
    e.preventDefault();
    if (form.permissions.length === 0) {
      toast.error('Grant at least one permission');
      return;
    }
    try {
      const res = await axios.post(`${API}/employee-invites`, {
        email: form.email,
        name: form.name,
        permissions: form.permissions,
      });
      const link = buildInviteLink(res.data.token);
      setGeneratedLink(link);
      toast.success('Invite created! Share the link below.');
      onInviteCreated?.();
    } catch (err) {
      toast.error(err.response?.data?.detail || 'Failed to create invite');
    }
  };

  return (
    <Dialog open={open} onOpenChange={handleOpenChange}>
      <DialogContent className="max-w-lg" data-testid="invite-dialog">
        <DialogHeader>
          <DialogTitle>Invite an Employee</DialogTitle>
        </DialogHeader>
        {generatedLink ? (
          <div className="space-y-4">
            <div className="flex flex-col items-center text-center py-4">
              <div className="w-14 h-14 rounded-full bg-green-100 flex items-center justify-center mb-3">
                <CheckCircle2 className="w-7 h-7 text-green-600" />
              </div>
              <h3 className="text-lg font-semibold">Invite created!</h3>
              <p className="text-sm text-muted-foreground mt-1">
                Share this link with your employee. It expires in 24 hours.
              </p>
            </div>
            <div className="flex justify-center py-2" data-testid="generated-invite-qr">
              <div className="p-4 bg-white rounded-xl border-2 border-primary/20 shadow-sm">
                <QRCodeSVG value={generatedLink} size={180} bgColor="#ffffff" fgColor="#1A3622" level="M" />
              </div>
            </div>
            <p className="text-xs text-muted-foreground text-center -mt-2">
              Have your employee scan this QR code with their phone camera
            </p>
            <div className="p-3 bg-muted/40 rounded-lg font-mono text-xs break-all" data-testid="generated-invite-link">
              {generatedLink}
            </div>
            <div className="flex gap-2">
              <Button data-testid="copy-generated-link-btn" onClick={() => copyLink(generatedLink)} className="flex-1 rounded-full bg-primary hover:bg-primary/90">
                <Copy className="w-4 h-4 mr-2" /> Copy Link
              </Button>
              <Button variant="outline" onClick={() => handleOpenChange(false)} className="flex-1 rounded-full">
                Done
              </Button>
            </div>
            <p className="text-xs text-muted-foreground text-center">
              Send the link via email, SMS, messaging app, or let them scan the QR code above.
            </p>
          </div>
        ) : (
          <form onSubmit={submit} className="space-y-4">
            <div>
              <Label>Employee Name</Label>
              <Input
                data-testid="invite-name-input"
                required
                value={form.name}
                onChange={(e) => setForm({ ...form, name: e.target.value })}
              />
            </div>
            <div>
              <Label>Email</Label>
              <Input
                data-testid="invite-email-input"
                type="email"
                required
                value={form.email}
                onChange={(e) => setForm({ ...form, email: e.target.value })}
              />
            </div>
            <div>
              <Label className="mb-2 block">Permissions</Label>
              <div className="space-y-2 border rounded-lg p-3 bg-muted/30">
                {permissions.map((perm) => (
                  <label key={perm.key} className="flex items-start gap-3 cursor-pointer hover:bg-white/60 p-2 rounded-md">
                    <Checkbox
                      data-testid={`invite-perm-${perm.key}`}
                      checked={form.permissions.includes(perm.key)}
                      onCheckedChange={() => togglePermission(perm.key)}
                    />
                    <div className="flex-1">
                      <p className="text-sm font-medium">{perm.label}</p>
                      <p className="text-xs text-muted-foreground">{perm.description}</p>
                    </div>
                  </label>
                ))}
              </div>
            </div>
            <div className="flex justify-end gap-2 pt-2">
              <Button type="button" variant="outline" onClick={() => handleOpenChange(false)}>Cancel</Button>
              <Button type="submit" data-testid="create-invite-btn" className="bg-primary hover:bg-primary/90">
                <Send className="w-4 h-4 mr-2" /> Generate Invite Link
              </Button>
            </div>
          </form>
        )}
      </DialogContent>
    </Dialog>
  );
}
/* eslint-enable react/prop-types */
